import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import TargetTitle from './TargetTitle';
import { getBatchStatus, getUsageRate } from '../utils/batchStatus';

const ActivationBatchTable = ({ 
  batches = [], 
  loading = false, 
  onRefresh, 
  pagination = null,
  onPageChange,
  onCreateBatch,
  onSearch
}) => {
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedBatches(batches.map(batch => batch._id || batch.id));
    } else {
      setSelectedBatches([]);
    }
  };

  const handleSelectBatch = (batchId, checked) => {
    if (checked) {
      setSelectedBatches(prev => [...prev, batchId]);
    } else {
      setSelectedBatches(prev => prev.filter(id => id !== batchId));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('vi-VN').format(num || 0);
  };

  const getBatchId = (batch) => batch._id || batch.id;

  const handleSearch = (e) => {
    e.preventDefault();
    onSearch?.(searchTerm);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const renderPagination = () => {
    if (!pagination || pagination.last_page <= 1) return null;

    const pages = [];
    const currentPage = pagination.current_page || 1;
    const lastPage = pagination.last_page || 1;

    // Previous button
    pages.push(
      <button
        key="prev"
        onClick={() => onPageChange?.(currentPage - 1)}
        disabled={currentPage <= 1}
        className="px-3 py-2 text-sm border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Trước
      </button>
    );

    // Page numbers
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(lastPage, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => onPageChange?.(i)}
          className={`px-3 py-2 text-sm border-t border-b border-gray-300 hover:bg-gray-50 ${
            i === currentPage 
              ? 'bg-blue-600 text-white border-blue-600' 
              : 'bg-white text-gray-700'
          }`}
        >
          {i}
        </button>
      );
    }

    // Next button
    pages.push(
      <button
        key="next"
        onClick={() => onPageChange?.(currentPage + 1)}
        disabled={currentPage >= lastPage}
        className="px-3 py-2 text-sm border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Sau
      </button>
    );

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
        <div className="text-sm text-gray-700">
          Hiển thị {pagination?.from || 0}-{pagination?.to || batches.length} của {pagination?.total || 0} kết quả
        </div>
        <div className="flex space-x-1">
          {pages}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      {/* Header */}
      <div className="p-6 border-b">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Lô mã kích hoạt</h3>
          <div className="flex items-center space-x-2">
            {selectedBatches.length > 0 && (
              <span className="text-sm text-gray-600">
                Đã chọn {selectedBatches.length} lô
              </span>
            )}
            <button
              onClick={onCreateBatch}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              + Tạo lô mã mới
            </button>
            <button
              onClick={onRefresh}
              className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              🔄 Làm mới
            </button>
          </div>
        </div>
        
        {/* Search Form */}
        <form onSubmit={handleSearch} className="flex items-center space-x-2">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Tìm kiếm theo tên lô, code prefix, code middle..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            🔍 Tìm kiếm
          </button>
          {searchTerm && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                onSearch?.('');
              }}
              className="px-3 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              ✕ Xóa
            </button>
          )}
        </form>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-12 px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={batches.length > 0 && selectedBatches.length === batches.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="rounded border-gray-300"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tên lô
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sách
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tổng mã
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Đã sử dụng
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Chưa sử dụng
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Đã thu hồi
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                title="Tính trên số mã còn khả dụng, không tính mã đã thu hồi"
              >
                Tỷ lệ sử dụng
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ngày tạo
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hết hạn
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Trạng thái
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="11" className="px-4 py-8 text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
                    Đang tải...
                  </div>
                </td>
              </tr>
            ) : batches.length === 0 ? (
              <tr>
                <td colSpan="11" className="px-4 py-8 text-center text-gray-500">
                  <div className="text-4xl mb-2">📦</div>
                  Không có lô mã nào
                </td>
              </tr>
            ) : (
              batches.map((batch) => {
                const batchId = getBatchId(batch);
                const usageRate = getUsageRate(batch);
                const status = getBatchStatus(batch);
                
                return (
                  <tr key={batchId} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedBatches.includes(batchId)}
                        onChange={(e) => handleSelectBatch(batchId, e.target.checked)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <Link
                        to={`/books/activation-codes/batch/${batchId}`}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {batch.name || 'N/A'}
                      </Link>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                        <TargetTitle item={batch} />
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {formatNumber(batch.total_codes)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {formatNumber(batch.used_codes)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {formatNumber(batch.unused_codes)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {formatNumber(batch.revoked_codes)}
                      {batch.expired_codes > 0 && (
                        <div className="text-xs text-gray-500">
                          + {formatNumber(batch.expired_codes)} mã hết hạn
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${usageRate}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600">{usageRate}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {formatDate(batch.created_at)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {formatDate(batch.expires_at)}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${status.className}`}>
                        {status.label}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {renderPagination()}
    </div>
  );
};

export default ActivationBatchTable;
